/**
 * In-place MFA step-up: raise the CURRENT session to AAL2 with a TOTP code,
 * without leaving the screen.
 *
 * Why not route to /mfa-verify: that screen is the sign-in step. On success it
 * navigates to the role home, and abandoning it discards the session — both are
 * right at sign-in and wrong in the middle of a flow (deleting the account,
 * cancelling a deletion), where the person must come back to the exact action
 * the server refused with 403 `aal2_required` and have it retried.
 *
 * The sequence is the one change-password.tsx and mfa-verify.tsx already use:
 * first verified TOTP factor → challenge → verify. After it, invalidate
 * `AAL_QUERY_KEY` so every guard re-reads the new level.
 */

import { supabase } from '@/lib/supabase/client';

export class StepUpUnavailableError extends Error {
  constructor() {
    super('no_verified_factor');
    this.name = 'StepUpUnavailableError';
  }
}

/** A TOTP code is exactly six digits. */
export function isTotpCode(code: string): boolean {
  return /^\d{6}$/.test(code.trim());
}

/** Raise this session to AAL2. Throws the GoTrue error on a wrong code. */
export async function completeStepUp(code: string): Promise<void> {
  const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
  if (listError) throw listError;
  const factor = factors?.totp?.find((f) => f.status === 'verified');
  if (!factor) throw new StepUpUnavailableError();

  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
  if (challengeError) throw challengeError;

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId: factor.id,
    challengeId: challenge.id,
    code: code.trim(),
  });
  if (verifyError) throw verifyError;
}
